import { FormEventHandler, useEffect, useMemo } from 'react'
import { useState } from 'react'
import styled from 'styled-components'
import { Button, Icon } from '@bm-js/atlascloud-ui'
import { breakPointsCss, colors, measurments } from '../utils/config'
import ImageInput from './ImageInput'
import Input from './Input'
import Checkbox from './Checkbox'
import Select from './Select'
import {
  CalendarBM,
  CalendarItem,
  DateString,
  parseDateTime,
  ChangeEvent,
  FileEntryFM,
  LucideIconName,
} from '@bm-js/atlascloud-shared'
import HTMLInput from './HTMLInput'
import { ONE_DAY, ONE_WEEK } from '../utils/date.util'
import dayjs from 'dayjs'
import { useToast } from '../utils/hooks'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

const StyledCalendarItemForm = styled.div`
  .repeat-options {
    margin-top: 0.4rem;
    margin-bottom: 1.6rem;
    border: 1px solid ${colors.gray400};
    border-radius: ${measurments.borderRadius}px;
    padding: 1rem 0.5rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: flex-start;
    gap: 1rem;
    .repeat-options-inner {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      span,
      select {
        font-size: 0.875rem;
        margin: 0;
      }
    }
  }
  ${breakPointsCss.tablet} {
    .repeat-options {
      grid-template-columns: 1fr;
    }
    .two-col {
      grid-template-columns: 1fr;
    }
  }
`

export type RepeatOptions = CalendarBM['items'][0]['repeatOptions']

type Props = {
  data?: CalendarItem
  handleSubmit: (
    formData: CalendarItemInput,
    successCallback: () => void,
    errorCallback: () => void,
    repeatOptions?: RepeatOptions
  ) => void
}

export type CalendarItemInput = Omit<
  CalendarItem,
  'repeatOptions' | '_id' | 'urlTitle' | 'repeatKey'
> & {
  urlTitle?: string
  _id?: string
}

const CalendarItemForm = ({ handleSubmit, data }: Props) => {
  const { tr } = useTr()
  const toast = useToast()

  const initialData: CalendarItemInput = {
    start: '',
    end: '',
    summary: '',
    bodyHTML: '',
    link: '',
    image: undefined,
    locationName: '',
    locationAddress: '',
  }

  const initialRepeatOptions: RepeatOptions = {
    unit: 'week',
    repeatUntil: '',
    cancelledDates: [],
  }

  const [formData, setFormData] = useState(initialData)
  const [isRepeating, setIsRepeating] = useState(false)
  const [repeatOptions, setRepeatOptions] = useState(initialRepeatOptions)

  const dateDiff = useMemo(() => {
    if (!formData.start || !formData.end) return 0
    return dayjs(formData.end).diff(dayjs(formData.start))
  }, [formData.end, formData.start])

  const [loading, setLoading] = useState(false)
  const [hasChanged, setHasChanged] = useState(true)

  const handleAddOmittedDate = (date: string) => {
    setHasChanged(true)
    setRepeatOptions({
      ...repeatOptions,
      cancelledDates: [...(repeatOptions.cancelledDates || []), date],
    })
  }

  const handleDeleteOmittedDate = (date: string) => {
    setHasChanged(true)
    setRepeatOptions({
      ...repeatOptions,
      cancelledDates: (repeatOptions.cancelledDates || []).filter(
        (d) => d !== date
      ),
    })
  }

  const cancelledDatesOptions = useMemo(() => {
    if (!repeatOptions.repeatUntil || !data?.end || !isRepeating) return []
    let currentStart = dayjs(data.start)
    const repeatUntilDate = dayjs(repeatOptions.repeatUntil)

    if (repeatUntilDate.isBefore(currentStart)) return []
    const dates: DateString[] = []
    while (
      currentStart.isBefore(repeatUntilDate) ||
      currentStart.isSame(repeatUntilDate, 'day')
    ) {
      dates.push(currentStart.format('YYYY-MM-DD'))
      currentStart = currentStart.add(1, repeatOptions.unit as 'day' | 'week')
    }
    return dates
      .filter((d) => !(repeatOptions.cancelledDates || []).includes(d))
      .map((d) => {
        return {
          value: d,
          label: d,
        }
      })
  }, [data, repeatOptions, isRepeating])

  const handleChange = (e: ChangeEvent<FileEntryFM | undefined | string>) => {
    setHasChanged(true)
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleRepeatOptionsChange = (e: ChangeEvent) => {
    setHasChanged(true)
    setRepeatOptions({
      ...repeatOptions,
      [e.target.name]: e.target.value,
    })
  }

  const preSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    const startDate = dayjs(formData.start)
    const endDate = dayjs(formData.end)

    if (endDate.isBefore(startDate)) {
      toast.error(tr(TrKey.calendarEndTimeBeforeStartTime))
      return
    }

    setLoading(true)
    handleSubmit(
      formData,
      () => {
        setFormData(initialData)
        setLoading(false)
      },
      () => {
        setLoading(false)
      },
      isRepeating ? repeatOptions : undefined
    )
  }

  const setEndTimePlusHours = (h: number) => {
    if (!formData.start) return

    const startTime = dayjs(formData.start)

    const newTime = dayjs(startTime).add(h, 'hour').format('YYYY-MM-DDTHH:mm')
    setHasChanged(true)
    setFormData({
      ...formData,
      end: newTime,
    })
  }

  useEffect(() => {
    setIsRepeating(false)
    if (data) {
      if (data.repeatOptions) {
        setIsRepeating(true)
        setRepeatOptions(data.repeatOptions)
      }
      setFormData(data)
      setHasChanged(false)
    } else {
      setFormData(initialData)
      setIsRepeating(false)
      setRepeatOptions(initialRepeatOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <StyledCalendarItemForm>
      <form onSubmit={preSubmit}>
        <h2 className='m0 primblue mbottom1'>
          {data ? tr(TrKey.edit) : tr(TrKey.create)} {tr(TrKey.calendarEvent)}
        </h2>
        <Input
          type='text'
          required
          disabled={loading}
          label={tr(TrKey.calendarEventSummaryLabel)}
          desc={tr(TrKey.calendarEventSummaryDescription)}
          name='summary'
          id='summary'
          value={formData.summary}
          onChange={handleChange}
        />

        <div className='two-col'>
          <Input
            type='datetime-local'
            required
            disabled={loading}
            label={tr(TrKey.calendarEventStartTimeLabel)}
            desc={tr(TrKey.calendarEventStartTimeDescription)}
            name='start'
            id='start'
            value={formData.start ? parseDateTime(formData.start) : ''}
            onChange={handleChange}
          />
          <div>
            <Input
              noMargin
              className='mbottom05'
              type='datetime-local'
              required
              disabled={loading}
              label={tr(TrKey.calendarEventEndTimeLabel)}
              desc={tr(TrKey.calendarEventEndTimeDescription)}
              name='end'
              id='end'
              value={formData.end ? parseDateTime(formData.end) : ''}
              onChange={handleChange}
            />
            {!!formData.start && (
              <div className='gap05 jce flex'>
                <Button onClick={() => setEndTimePlusHours(1)}>1H</Button>
                <Button onClick={() => setEndTimePlusHours(2)}>2H</Button>
                <Button onClick={() => setEndTimePlusHours(3)}>3H</Button>
              </div>
            )}
          </div>
        </div>

        <Checkbox
          disabled={loading}
          noMargin={isRepeating}
          label={tr(TrKey.calendarEventRepeatOptionsLabel)}
          desc={tr(TrKey.calendarEventRepeatOptionsDescription)}
          name='isRepeating'
          id='isRepeating'
          checked={isRepeating}
          onChange={(e) => {
            setIsRepeating(e.target.checked)
            setHasChanged(true)
          }}
        />
        {isRepeating && (
          <div className='repeat-options'>
            <div className='repeat-options-inner'>
              <span>{tr(TrKey.calendarEventRepeatEvery)}</span>
              <Select
                light
                noMargin
                value={repeatOptions.unit}
                name='unit'
                required
                onChange={handleRepeatOptionsChange}
              >
                <option value='day' disabled={dateDiff > ONE_DAY}>
                  {tr(TrKey.day)}
                </option>
                <option value='week' disabled={dateDiff > ONE_WEEK}>
                  {tr(TrKey.week)}
                </option>
              </Select>
              <span>{tr(TrKey.calendarRepeatOptionsUntil)}</span>
              <Input
                type='date'
                required
                value={repeatOptions.repeatUntil}
                name='repeatUntil'
                light
                noMargin
                onChange={handleRepeatOptionsChange}
              />
            </div>
            <div>
              <p className='label'>
                {tr(TrKey.calendarRepeatOptionsCancelledDatesLabel)}
              </p>
              <p className='desc'>
                {tr(TrKey.calendarRepeatOptionsCancelledDatesDescription)}
              </p>
              <div className='mt-4'>
                {(repeatOptions.cancelledDates || []).map((date) => (
                  <div key={date} className='flex items-center gap-2'>
                    <p className='m-0 text-sm'>{date}</p>
                    <button
                      onClick={() => handleDeleteOmittedDate(date)}
                      className='text-red-400'
                    >
                      <Icon name={LucideIconName.X} className='size-5' />
                    </button>
                  </div>
                ))}
              </div>
              <Select
                noMargin
                value=''
                name='cancelledDates'
                className='mt-4 !w-48'
                onChange={(e) => handleAddOmittedDate(e.target.value)}
              >
                <option value='' disabled>
                  {tr(TrKey.calendarRepeatOptionsCancelledDatesPlaceholder)}
                </option>
                {cancelledDatesOptions.map((date) => (
                  <option key={date.value} value={date.value}>
                    {date.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )}
        <div className='two-col'>
          <Input
            type='text'
            disabled={loading}
            name={'locationName'}
            value={formData.locationName}
            id='locationName'
            onChange={handleChange}
            label={tr(TrKey.calendarLocationNameLabel)}
            desc={tr(TrKey.calendarLocationNameDescription)}
          />
          <Input
            type='text'
            disabled={loading}
            name={'locationAddress'}
            value={formData.locationAddress}
            id='locationAddress'
            onChange={handleChange}
            label={tr(TrKey.calendarLocationAddressLabel)}
            desc={tr(TrKey.calendarLocationAddressDescription)}
          />
        </div>
        <ImageInput
          label={tr(TrKey.image)}
          file={formData.image}
          onChange={(f) => {
            const e = {
              target: {
                name: 'image',
                value: f,
              },
            } as ChangeEvent<FileEntryFM | undefined>
            handleChange(e)
          }}
        />
        <HTMLInput
          label={tr(TrKey.breadText)}
          desc={tr(TrKey.calendarBreadTextDescription)}
          id='bodyHTML'
          name='bodyHTML'
          value={formData.bodyHTML}
          onChange={handleChange}
        />
        <div className='jce flex'>
          <Button type='submit' disabled={loading || !hasChanged}>
            {loading ? tr(TrKey.saving) : tr(TrKey.save)}
          </Button>
        </div>
      </form>
    </StyledCalendarItemForm>
  )
}

export default CalendarItemForm
